import React, { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "../lib/api";
import { Search, FileDown, FileUp, Sparkles, Trash2, Globe, Lock } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function fmtUSD(n) {
  if (!n) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n}`;
}

export default function Portfolio() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [ipType, setIpType] = useState("");
  const [country, setCountry] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [valOpen, setValOpen] = useState(false);
  const [valLoading, setValLoading] = useState(false);
  const [valReport, setValReport] = useState(null);
  const [valAsset, setValAsset] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (ipType) params.ip_type = ipType;
      if (country) params.country = country;
      if (status) params.status = status;
      const { data } = await api.get("/ip/assets", { params });
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ipType, country, status]);

  const countries = useMemo(() => [...new Set(rows.map((r) => r.country))].sort(), [rows]);

  const runValuation = async (asset) => {
    setValAsset(asset);
    setValOpen(true);
    setValLoading(true);
    setValReport(null);
    try {
      const { data } = await api.post(`/ai/valuation/${asset.id}`);
      setValReport(data);
      toast.success("AI valuation complete");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
      setValOpen(false);
    } finally {
      setValLoading(false);
    }
  };

  const deleteAsset = async (id) => {
    if (!window.confirm("Delete this IP asset?")) return;
    try {
      await api.delete(`/ip/assets/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const togglePublic = async (asset) => {
    try {
      const { data } = await api.post(`/ip/assets/${asset.id}/toggle-public`);
      toast.success(data.is_public ? "Now public in gallery" : "Made private");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportCsv = async () => {
    try {
      const resp = await api.get("/portfolio/export", { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "calcutta-portfolio.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Exported");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/portfolio/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.created} asset${data.created !== 1 ? "s" : ""}`);
      e.target.value = "";
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-6 md:p-8 fade-in-up" data-testid="portfolio-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="eyebrow">Portfolio Manager</div>
          <h1 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight mt-1">
            Your IP Assets
          </h1>
          <p className="text-slate-600 mt-2 text-sm md:text-base">
            Filter, value, and manage renewals across every asset.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} className="text-sm border border-slate-300 px-3 py-2 rounded-sm hover:border-[#B33A3A] hover:text-[#B33A3A] flex items-center gap-1.5" data-testid="export-csv-btn">
            <FileDown size={14} /> Export CSV
          </button>
          <label className="text-sm border border-slate-300 px-3 py-2 rounded-sm hover:border-[#B33A3A] hover:text-[#B33A3A] flex items-center gap-1.5 cursor-pointer" data-testid="import-csv-label">
            <FileUp size={14} /> Import CSV
            <input type="file" accept=".csv" onChange={importCsv} className="hidden" data-testid="import-csv-input" />
          </label>
        </div>
      </div>

      {/* Filters */}
      <div className="bengal-card p-4 mb-5 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <Search size={16} className="text-slate-400" />
          <input
            placeholder="Search title…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            data-testid="portfolio-search-input"
            className="flex-1 text-sm focus:outline-none bg-transparent"
          />
        </div>
        <select value={ipType} onChange={(e) => setIpType(e.target.value)} data-testid="filter-type" className="border border-slate-300 rounded-sm text-sm px-3 py-2 bg-white">
          <option value="">All types</option>
          <option value="patent">Patents</option>
          <option value="trademark">Trademarks</option>
          <option value="copyright">Copyrights</option>
        </select>
        <select value={country} onChange={(e) => setCountry(e.target.value)} data-testid="filter-country" className="border border-slate-300 rounded-sm text-sm px-3 py-2 bg-white">
          <option value="">All countries</option>
          {countries.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filter-status" className="border border-slate-300 rounded-sm text-sm px-3 py-2 bg-white">
          <option value="">All status</option>
          <option value="granted">Granted</option>
          <option value="registered">Registered</option>
          <option value="pending">Pending</option>
        </select>
        <button onClick={load} className="bengal-btn-primary text-sm" data-testid="apply-filters-btn">Apply</button>
      </div>

      {/* Table */}
      <div className="bengal-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left" data-testid="portfolio-table">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wider border-b border-slate-200">
              <tr>
                <th className="py-3 px-4">CAL ID</th>
                <th className="py-3 px-4">Title</th>
                <th className="py-3 px-4">Type</th>
                <th className="py-3 px-4">Category</th>
                <th className="py-3 px-4">Country</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4">Filing #</th>
                <th className="py-3 px-4 text-right">Valuation</th>
                <th className="py-3 px-4">Renewal</th>
                <th className="py-3 px-4" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={10} className="text-center text-slate-500 py-10">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={10} className="text-center text-slate-500 py-10">No assets match the filters.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`asset-row-${r.id}`}>
                  <td className="py-3 px-4 font-mono text-[11px] text-slate-500">{r.cal_id || "—"}</td>
                  <td className="py-3 px-4 font-medium text-slate-900 max-w-[240px]"><div className="truncate" title={r.title}>{r.title}</div></td>
                  <td className="py-3 px-4 capitalize">{r.ip_type}</td>
                  <td className="py-3 px-4 text-slate-600">{r.category}</td>
                  <td className="py-3 px-4 font-mono text-xs">{r.country}</td>
                  <td className="py-3 px-4">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${
                      r.status === "granted" || r.status === "registered"
                        ? "bg-green-50 text-green-800 border-green-200"
                        : "bg-amber-50 text-amber-800 border-amber-200"
                    }`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 font-mono text-xs text-slate-600">{r.filing_number || "—"}</td>
                  <td className="py-3 px-4 text-right font-mono text-[#B33A3A] font-semibold">{fmtUSD(r.valuation_usd)}</td>
                  <td className="py-3 px-4 text-xs text-slate-500">{r.renewal_due || "—"}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => togglePublic(r)}
                        title={r.is_public ? "Make private" : "Publish to gallery"}
                        data-testid={`public-btn-${r.id}`}
                        className={`p-1.5 rounded-sm ${r.is_public ? "text-[#B33A3A] bg-red-50" : "text-slate-500 hover:text-[#B33A3A] hover:bg-red-50"}`}
                      >
                        {r.is_public ? <Globe size={15} /> : <Lock size={15} />}
                      </button>
                      <button
                        onClick={() => runValuation(r)}
                        title="AI Valuation"
                        data-testid={`value-btn-${r.id}`}
                        className="p-1.5 text-slate-500 hover:text-[#B33A3A] hover:bg-red-50 rounded-sm"
                      >
                        <Sparkles size={15} />
                      </button>
                      <button
                        onClick={() => deleteAsset(r.id)}
                        title="Delete"
                        data-testid={`delete-btn-${r.id}`}
                        className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-sm"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={valOpen} onOpenChange={setValOpen}>
        <DialogContent className="max-w-2xl" data-testid="valuation-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {valLoading ? "Running AI Valuation…" : "Valuation Report"}
            </DialogTitle>
          </DialogHeader>
          {valAsset && <div className="eyebrow">{valAsset.title}</div>}
          {valLoading && (
            <div className="py-8 text-center text-slate-500 text-sm">
              Claude Sonnet 4.5 is analyzing market demand, citation strength, and licensing potential…
            </div>
          )}
          {valReport && !valLoading && (
            <div className="space-y-5 pt-2">
              <div className="flex items-end gap-4 flex-wrap">
                <div>
                  <div className="eyebrow">Estimated Value</div>
                  <div className="font-mono text-3xl text-[#B33A3A] font-semibold" data-testid="valuation-amount">{fmtUSD(valReport.valuation_usd)}</div>
                </div>
                <div>
                  <div className="eyebrow">Confidence</div>
                  <div className="font-mono text-lg">{Math.round((valReport.confidence || 0) * 100)}%</div>
                </div>
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">{valReport.summary}</p>
              <div className="space-y-2">
                <div className="eyebrow">Factors</div>
                {(valReport.factors || []).map((f, i) => (
                  <div key={i}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-slate-800 font-medium">{f.label}</span>
                      <span className="font-mono text-slate-500">{f.score}/100</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-sm overflow-hidden">
                      <div style={{ width: `${f.score}%`, background: "#B33A3A" }} className="h-full" />
                    </div>
                    {f.note && <div className="text-xs text-slate-500 mt-1">{f.note}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
